import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { openUatuWithMessage } from './UatuGreetingBubble';

/**
 * 🧿 UatuNudgeManager
 * -------------------
 * Composant INVISIBLE qui pousse des "nudges" bienveillants d'Uatu tout au long
 * de la session. Différenciant Valnexo : Uatu ne se contente pas de saluer une
 * fois au démarrage, il RESTE PRÉSENT et propose son aide plusieurs fois par
 * session — jamais relou, toujours bienveillant.
 *
 * Déclencheurs :
 *  - `inactivity`         : après 12 min sans clic ni mouvement souris (pousse le nudge critical)
 *  - `navigation`         : parfois lors du changement de page (30% chance, cooldown 8 min)
 *  - `action_completed`   : quand un event `uatu:action-done` est dispatché ailleurs
 *  - `idle_long`          : après 30 min de session sans avoir ouvert le chat Uatu
 *
 * Garde-fous :
 *  - Max 4 nudges par session (fenêtre navigateur)
 *  - Cooldown 8 min entre 2 nudges
 *  - Toast dismissible via bouton × (ne compte pas comme "vu")
 *  - Silencieux si l'onglet n'est pas actif (document.visibilityState)
 */
const MAX_NUDGES_PER_SESSION = 4;
const COOLDOWN_MS = 8 * 60 * 1000;
const INACTIVITY_MS = 12 * 60 * 1000;
const IDLE_LONG_MS = 30 * 60 * 1000;
const NAV_CHANCE = 0.3;

export default function UatuNudgeManager() {
    const [nudges, setNudges] = useState([]);
    const [tenantName, setTenantName] = useState('Uatu');
    const usedNudgesRef = useRef(new Set());     // triggers déjà consommés
    const nudgeCountRef = useRef(0);
    const lastNudgeAtRef = useRef(0);
    const lastActivityRef = useRef(Date.now());
    const sessionStartRef = useRef(Date.now());
    const location = useLocation();

    // Charge la banque de nudges au boot
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await api.get('/dashboard/ai-briefing');
                if (!mounted) return;
                setNudges(res.data.nudge_bank || []);
                if (res.data.assistant_name) setTenantName(res.data.assistant_name);
            } catch { /* noop */ }
        })();
        return () => { mounted = false; };
    }, []);

    // Récupère un nudge disponible pour un trigger donné
    const pickNudge = useCallback((trigger) => {
        // Match exact d'abord
        let candidate = nudges.find(
            (n) => n.trigger === trigger && !usedNudgesRef.current.has(n.trigger + '|' + n.message)
        );
        // Fallback : n'importe quel nudge non utilisé
        if (!candidate) {
            candidate = nudges.find(
                (n) => !usedNudgesRef.current.has(n.trigger + '|' + n.message)
            );
        }
        return candidate;
    }, [nudges]);

    const canFire = useCallback(() => {
        if (nudgeCountRef.current >= MAX_NUDGES_PER_SESSION) return false;
        if (Date.now() - lastNudgeAtRef.current < COOLDOWN_MS) return false;
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false;
        return true;
    }, []);

    const fireNudge = useCallback((trigger) => {
        if (!canFire()) return;
        const nudge = pickNudge(trigger);
        if (!nudge) return;

        usedNudgesRef.current.add(nudge.trigger + '|' + nudge.message);
        nudgeCountRef.current += 1;
        lastNudgeAtRef.current = Date.now();

        toast.custom(
            (t) => (
                <div
                    data-testid={`uatu-nudge-${nudge.trigger}`}
                    className="pointer-events-auto max-w-md rounded-2xl border border-violet-200 shadow-2xl overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 100%)',
                        boxShadow: '0 20px 40px rgba(124, 58, 237, 0.20), 0 4px 8px rgba(0,0,0,0.05)',
                    }}
                >
                    <div className="p-4">
                        <div className="flex items-start gap-3">
                            {/* Avatar mini Uatu */}
                            <div
                                className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center shadow-md"
                                style={{
                                    background: 'linear-gradient(135deg, #1E1B4B 0%, #7C3AED 100%)',
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 5c-5 0-9 4-10 7 1 3 5 7 10 7s9-4 10-7c-1-3-5-7-10-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="12" r="3" fill="white" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wider mb-0.5">
                                    {tenantName} · le Watcher
                                </p>
                                <p className="text-sm text-slate-800 leading-snug">
                                    {nudge.message}
                                </p>
                                <div className="mt-2.5 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            openUatuWithMessage(nudge.seed_message || '');
                                            toast.dismiss(t);
                                        }}
                                        className="px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow"
                                        style={{
                                            background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
                                        }}
                                        data-testid="uatu-nudge-accept"
                                    >
                                        {nudge.cta || 'Passe-moi la main'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toast.dismiss(t)}
                                        className="px-3 py-1.5 rounded-lg text-slate-500 hover:text-slate-800 text-xs font-medium"
                                        data-testid="uatu-nudge-dismiss"
                                    >
                                        Plus tard
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
            { duration: 12000, position: 'bottom-right' }
        );
    }, [canFire, pickNudge, tenantName]);

    // Track activité utilisateur (souris + clavier + clic)
    useEffect(() => {
        const bump = () => { lastActivityRef.current = Date.now(); };
        const events = ['mousemove', 'keydown', 'click', 'scroll'];
        events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
        return () => {
            events.forEach((e) => window.removeEventListener(e, bump));
        };
    }, []);

    // Trigger 1 — INACTIVITY : check toutes les 60s si l'user est inactif depuis >12min
    useEffect(() => {
        if (nudges.length === 0) return undefined;
        const iv = setInterval(() => {
            if (Date.now() - lastActivityRef.current > INACTIVITY_MS) {
                fireNudge('inactivity');
                lastActivityRef.current = Date.now(); // reset pour éviter re-fire immédiat
            }
        }, 60_000);
        return () => clearInterval(iv);
    }, [nudges, fireNudge]);

    // Trigger 2 — IDLE_LONG : après 30 min de session
    useEffect(() => {
        if (nudges.length === 0) return undefined;
        const timeout = setTimeout(() => {
            fireNudge('idle_long');
        }, IDLE_LONG_MS);
        return () => clearTimeout(timeout);
    }, [nudges, fireNudge]);

    // Trigger 3 — NAVIGATION : nudge parfois lors du changement de route
    useEffect(() => {
        // Skip la 1re render (mount)
        if (Date.now() - sessionStartRef.current < 3000) return;
        if (Math.random() < NAV_CHANCE) {
            fireNudge('navigation');
        }
    }, [location.pathname, fireNudge]);

    // Trigger 4 — ACTION_COMPLETED : écoute un event global
    useEffect(() => {
        const handler = () => fireNudge('action_completed');
        window.addEventListener('uatu:action-done', handler);
        return () => window.removeEventListener('uatu:action-done', handler);
    }, [fireNudge]);

    return null; // Invisible
}
